import { TERMS_VERSION, TORN_TERMS_PAIRS } from './key-terms';
/**
 * The Omni-Torn website: its sections, what each needs, and the small pieces of data the
 * website, the server and the extension all share (history snapshots, the player's website
 * preferences). Browser-safe (no node: imports) — used by apps/web, apps/api and the extension.
 *
 * Two things decide whether a section opens:
 *  1. The Torn key's access (Public Only → Minimal → Limited → Full; a Custom key by the
 *     selections it was given). Too low: the section is greyed out with a lock and says which
 *     key it needs.
 *  2. The player's Omni-Torn level (owner, developer, alpha, beta, exclusive, basic): each
 *     section has a default, and the owner can change it in "Who gets what" (feature id
 *     `web-<section>`, the same access table the extension uses).
 */
import { canUseFeature, levelsFor, mayUseOmniTorn, NO_OVERRIDES, type Access, type AccessTable, type Level, type MyAccess } from './levels';

// ---------------------------------------------------------------- Torn key access

export type KeyLevel = 1 | 2 | 3 | 4;

export const KEY_LEVEL_NAMES: Record<KeyLevel, string> = {
  1: 'Public Only',
  2: 'Minimal Access',
  3: 'Limited Access',
  4: 'Full Access',
};

/** Short form for lock notes ("Needs a Limited Access key"). */
export const KEY_LEVEL_SHORT: Record<KeyLevel, string> = { 1: 'Public', 2: 'Minimal', 3: 'Limited', 4: 'Full' };

/** Torn's own "new key" page with the level filled in (the player names and makes the key there). */
export function keyLink(level: KeyLevel, title = 'OmniTorn'): string {
  return `https://www.torn.com/preferences.php#tab=api?step=addNewKey&title=${encodeURIComponent(title)}&type=${level}`;
}

export interface KeyAccess {
  level: KeyLevel;
  /** Torn's own name for it ("Limited Access", "Custom"…). */
  type: string;
  custom: boolean;
  /** Selections the key may use, per category (only meaningful for custom keys). */
  selections: Record<string, string[]>;
  /** Whose key it is, when Torn says. */
  playerId: number | null;
  factionId: number | null;
  /** The key's faction permission (Torn's "faction API access" for this member). */
  faction: boolean;
}

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const int = (x: unknown): number | null => {
  const n = typeof x === 'string' && /^\d+$/.test(x) ? Number(x) : x;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : null;
};

/** Torn's key info (v2 `/key/info`, or v1 `/key/?selections=info`) → what the key can read. Null if it isn't one. */
export function parseKeyInfo(body: unknown): KeyAccess | null {
  const root = obj(body);
  if (!root || root.error) return null;
  const info = obj(root.info) ?? root;
  const access = obj(info.access);
  const rawLevel = int(access?.level ?? info.access_level);
  const type = String(access?.type ?? info.access_type ?? '');
  const custom = /custom/i.test(type);
  const selections: Record<string, string[]> = {};
  const sel = obj(info.selections);
  if (sel) for (const [cat, list] of Object.entries(sel)) if (Array.isArray(list)) selections[cat] = list.filter((s): s is string => typeof s === 'string').map((s) => s.toLowerCase());
  // A custom key may report any level: judge it by what it can do, but never above Full.
  const byType: KeyLevel | null = /full/i.test(type) ? 4 : /limited/i.test(type) ? 3 : /minimal/i.test(type) ? 2 : /public/i.test(type) ? 1 : null;
  const level = (Math.min(4, Math.max(1, rawLevel ?? byType ?? 1)) as KeyLevel);
  if (rawLevel === null && byType === null && !custom) return null;
  const user = obj(info.user);
  return {
    level,
    type: type || KEY_LEVEL_NAMES[level],
    custom,
    selections,
    playerId: int(user?.id),
    factionId: int(user?.faction_id),
    faction: access?.faction === true,
  };
}

/** What a section or card needs from the key. */
export interface KeyNeed {
  level: KeyLevel;
  /** The /user selections it reads (a custom key is judged by these). */
  user?: readonly string[];
}

export function keyMeets(key: KeyAccess | null, need: KeyNeed): boolean {
  if (!key) return false;
  if (key.custom && need.user?.length && key.selections.user?.length) return need.user.every((s) => key.selections.user!.includes(s));
  return key.level >= need.level;
}

/** "Needs a Limited Access key" (custom keys: which selections are missing). */
export function keyNeedText(key: KeyAccess | null, need: KeyNeed): string {
  if (key?.custom && need.user?.length && key.selections.user?.length) {
    const missing = need.user.filter((s) => !key.selections.user!.includes(s));
    return `Needs a ${KEY_LEVEL_NAMES[need.level]} key (your custom key has no ${missing.join(', ')})`;
  }
  return `Needs a ${KEY_LEVEL_NAMES[need.level]} key`;
}

// ---------------------------------------------------------------- Omni-Torn levels

/** What a section needs by default (none: everyone) — the same rules as the extension's features. */
export type WebAccess = Access;

export function webLevelsFor(access: WebAccess | undefined): readonly Level[] {
  return levelsFor(access);
}

// ---------------------------------------------------------------- sections

export type WebSectionId =
  | 'overview'
  | 'stats'
  | 'battle'
  | 'attacks'
  | 'logs'
  | 'mycrimes'
  | 'energy'
  | 'events'
  | 'racing'
  | 'itemstats'
  | 'trading'
  | 'company'
  | 'faction'
  | 'travel'
  | 'market'
  | 'pricelist'
  | 'stocks'
  | 'lookup'
  | 'loadouts'
  | 'spies'
  | 'loot'
  | 'targets'
  | 'leaderboards'
  | 'calc'
  | 'items'
  | 'bank'
  | 'honors'
  | 'hof'
  | 'factions'
  | 'rankedwars'
  | 'territory'
  | 'crimes'
  | 'merits'
  | 'respect'
  | 'elo'
  | 'alerts'
  | 'account'
  | 'owner'
  | 'bot-stats';

export interface WebSection {
  id: WebSectionId;
  /** Feature id in the access table ("Who gets what"). */
  featureId: `web-${WebSectionId}`;
  name: string;
  /** Path on the website. */
  path: string;
  /** One short line under the name (menu tooltip, locked note). */
  blurb: string;
  key: KeyNeed;
  /** Default Omni-Torn level need (none: every level). */
  access?: WebAccess;
  /** Only for players in a faction. */
  faction?: boolean;
  /** Never locked by the access table (Account: everyone must be able to sign out and delete their data). */
  always?: boolean;
  group: 'you' | 'faction' | 'tools' | 'torn' | 'site';
}

export const WEB_SECTIONS: readonly WebSection[] = [
  { id: 'overview', featureId: 'web-overview', name: 'Overview', path: '/', blurb: 'Status, bars, cooldowns, flight, refills, money and networth at a glance.', key: { level: 1 }, group: 'you' },
  { id: 'stats', featureId: 'web-stats', name: 'Personal stats', path: '/stats', blurb: 'Every personal stat with today, 7-day and 30-day changes and charts.', key: { level: 1, user: ['personalstats'] }, group: 'you' },
  { id: 'battle', featureId: 'web-battle', name: 'Battle stats', path: '/battle', blurb: 'Strength, defense, speed and dexterity, with their history.', key: { level: 3, user: ['battlestats'] }, group: 'you' },
  { id: 'attacks', featureId: 'web-attacks', name: 'Attacks', path: '/attacks', blurb: 'Your fights and mugs: per day, results, respect, money mugged and by opponent.', key: { level: 3, user: ['attacks'] }, group: 'you' },
  { id: 'logs', featureId: 'web-logs', name: 'Logs', path: '/logs', blurb: 'Your activity log, sorted and summed up — shown only to you.', key: { level: 4, user: ['log'] }, group: 'you' },
  // Website roadmap S09 (torn.report's Crimes, Crimes Lite, Uniques): your own key only; the log part (Full key) keeps your
  // log in this browser (IndexedDB) — nothing new on the server. (`crimes` is Torn → Organized crimes.)
  { id: 'mycrimes', featureId: 'web-mycrimes', name: 'Your crimes', path: '/mycrimes', blurb: 'Every crime: skill, nerve, outcomes, money and items per nerve, uniques found — and with a Full key your log of them over any dates, kept in your browser.', key: { level: 2, user: ['crimes'] }, group: 'you' },
  // Website roadmap S12 (torn.report's Energy, Gym, Nerve, Drugs): all-time totals from personal stats (any key); the rest
  // from your log (Full key), kept in this browser by the log kit (S09) — nothing new on the server.
  { id: 'energy', featureId: 'web-energy', name: 'Energy & nerve', path: '/energy', blurb: 'Where your energy and nerve came from and went: the gym per stat and happy jumps, crimes and busts, drugs and overdoses — from your log over any dates, kept in your browser.', key: { level: 1, user: ['personalstats'] }, group: 'you' },
  // Website roadmap S16 (torn.report's Christmas Town, Easter Eggs, Trick or Treat; Torn Intel's basket guide): Torn's
  // calendar with your key; what each event gave you from your log (Full key), kept in this browser by the log kit — nothing
  // new on the server. Event target lists are the Target finder's presets.
  // (+ S38, torn.report's Hunting and Dump Search and the City Finder log: tabs Hunting, Dump and City finds.)
  { id: 'events', featureId: 'web-events', name: 'Events', path: '/events', blurb: 'Christmas Town, Trick or Treat and Easter eggs from your log: what each gave you every year, the basket guide, treats per hit and per 100 energy — and your hunting (profit per day, World Tiger Day), dump searches and city finds.', key: { level: 1 }, group: 'you' },
  // Website roadmap S37 (torn.report's racing page): Torn's racing selections with your key (your races with every
  // racer's result, best laps, cars, skill and points) and your log's Racing lines (Full key), kept in this browser by
  // the log kit — nothing new on the server.
  // Website roadmap S39 (torn.report's Item Stats): your weapons and armor (v2 user/equipment and user/inventory with your
  // key, Minimal) and each copy's details and usage (torn/{uids}/itemdetails and itemstats); each read a snapshot per item —
  // kept for you on the server from terms 19 (own-history stream `items`, with what the extension saw on your own Items
  // page), in this browser below it.
  { id: 'itemstats', featureId: 'web-itemstats', name: 'Item stats', path: '/itemstats', blurb: 'Your weapons and armor with their use over time — hits, critical hits, damage, rounds fired, hits taken, first owner, when made, bonuses — two dates side by side, new items marked.', key: { level: 2 }, group: 'you' },
  { id: 'racing', featureId: 'web-racing', name: 'Racing', path: '/racing', blurb: 'Your races per track (Uptown … Convict): results, wins, best laps, the cars you drove, racing skill and points — from Torn and your log.', key: { level: 2 }, group: 'you' },
  // Website roadmap S26 (torn.report's Item Market and Bazaar pages): your bazaar (any key) and Item Market listings
  // (Limited) against the cheapest listing (S11's server read, on your click); your buys and sales from your log (Full
  // key), kept in this browser by the log kit (S09) — your own key only, nothing new on the server.
  { id: 'trading', featureId: 'web-trading', name: 'Trading', path: '/trading', blurb: 'Your Item Market, bazaar and trades: what you bought and sold, fees, profit per sale, item and day (first in, first out, with your trade receipts), and your listings against the cheapest.', key: { level: 1 }, group: 'you' },
  { id: 'company', featureId: 'web-company', name: 'Company', path: '/company', blurb: 'Your company: employees, stock and its history; a daily tracker (profit, recaps, heatmap, star forecast, pricing advice); the best position for each employee, an efficiency calculator, and every company of a type.', key: { level: 1 }, group: 'you' },
  { id: 'faction', featureId: 'web-faction', name: 'Faction', path: '/faction', blurb: 'War, enemy travel and pushes, crimes, armory and its history, chains, the bank, raids and members — the Discord bot’s watch and what faction mates share.', key: { level: 1 }, faction: true, group: 'faction' },
  { id: 'travel', featureId: 'web-travel', name: 'Travel', path: '/travel', blurb: 'Your flight, stock abroad with profits per trip and hour, restock forecasts, when to leave, a day plan — and your travel history from your log (trips per country, buys abroad, profit, flights per day).', key: { level: 2, user: ['travel'] }, group: 'tools' },
  // Website roadmap S11 (weav3r's price finder): Item Market listings read by the server on demand (public data, lent keys
  // or its own key), bazaar listings only through the player's own extension (weav3r.dev), sell suggestions, categories.
  { id: 'market', featureId: 'web-market', name: 'Market', path: '/market', blurb: 'Any item’s prices: the cheapest Item Market and bazaar listings, market value, what to list it for, every item by category — and Gear (weapons and armor for sale by rarity and bonus, with RW values) and Deals (under value, $1 bazaars, your favorites).', key: { level: 1 }, group: 'tools' },
  // Website roadmap S33 (weav3r's Pricelists, trader discovery): traders' own lists of what they pay (fixed or a % of market
  // value that follows it), kept on the server from terms 16 (apps/api/src/pricelists.ts); /pricelist/{id}, /pricelist/manage.
  { id: 'pricelist', featureId: 'web-pricelist', name: 'Pricelists', path: '/pricelist', blurb: 'Traders’ pricelists: what each pays per item (fixed or a share of market value, kept up to date), the traders buying now — and your own list.', key: { level: 1 }, group: 'tools' },
  { id: 'stocks', featureId: 'web-stocks', name: 'Stocks', path: '/stocks', blurb: 'Your shares, prices over time, what each investment returns, rules of thumb.', key: { level: 3, user: ['stocks'] }, group: 'tools' },
  { id: 'lookup', featureId: 'web-lookup', name: 'Player lookup', path: '/lookup', blurb: 'Any player by name or id: profile, Omni-Torn’s estimate and its history, what they did over 7/30/90 days, the loadout last seen, and side by side with anyone.', key: { level: 1 }, group: 'tools' },
  // Website roadmap S17 (SR Tools' Loadout Intel and Weapon Catalog, Torn Intel's enemy loadouts): the loadouts Omni-Torn
  // users saw in fights (terms 3, a month) — read on the server, invite-only; a "Share" link opens only when signed in.
  { id: 'loadouts', featureId: 'web-loadouts', name: 'Loadouts', path: '/loadouts', blurb: 'What players were seen wearing in fights: search by player or faction, weapon, armor or bonus; each player’s changes; every weapon seen with its bonuses.', key: { level: 1 }, group: 'tools' },
  // Torn's own public lists (the server reads them with its own key: /api/torn/*) and calculators — any key will do.
  { id: 'items', featureId: 'web-items', name: 'Items', path: '/items', blurb: 'Every item: market value, shop prices, circulation — search and sort.', key: { level: 1 }, group: 'torn' },
  { id: 'bank', featureId: 'web-bank', name: 'Bank & shops', path: '/bank', blurb: 'Torn’s bank rates with what an investment earns, and the city shops’ prices and stock.', key: { level: 1 }, group: 'torn' },
  { id: 'honors', featureId: 'web-honors', name: 'Honors & medals', path: '/honors', blurb: 'Every honor and medal, how rare it is, and how to get it.', key: { level: 1 }, group: 'torn' },
  { id: 'hof', featureId: 'web-hof', name: 'Hall of Fame', path: '/hof', blurb: 'Torn’s Hall of Fame boards, and any player’s places in them.', key: { level: 1 }, group: 'torn' },
  // Website roadmap S14 (FF Scouter's Faction Profile / Compare Factions): any faction's public profile read by the server
  // on demand (lent keys or its own key, kept an hour), the player database's estimates of its members and their spread.
  { id: 'factions', featureId: 'web-factions', name: 'Factions', path: '/factions', blurb: 'Any faction: profile, members with Omni-Torn’s estimates and how they spread, ranked wars, the chain — two side by side; the top factions and the ranked wars going on.', key: { level: 1 }, group: 'torn' },
  // Website roadmap S15 (FF Scouter's Active Ranked Wars, TornStats' ranked wars and Elo): Torn's list of ranked wars and a
  // war's report, read by the server with its own key (kept 15 minutes; reports on demand, in memory).
  { id: 'rankedwars', featureId: 'web-rankedwars', name: 'Ranked wars', path: '/rankedwars', blurb: 'Every ranked war going on, starting within 12 hours and just ended: scores, how close each is to its end, the war’s report — and two factions side by side.', key: { level: 1 }, group: 'torn' },
  { id: 'territory', featureId: 'web-territory', name: 'Territory & rackets', path: '/territory', blurb: 'Rackets, who holds them, and the territory wars going on.', key: { level: 1 }, group: 'torn' },
  { id: 'crimes', featureId: 'web-crimes', name: 'Organized crimes', path: '/crimes', blurb: 'Every organized crime: its level, roles, the items they need, how much each role matters and the pass rate each role needs.', key: { level: 1 }, group: 'torn' },
  { id: 'merits', featureId: 'web-merits', name: 'Merit planner', path: '/merits', blurb: 'Plan your merits: what each level costs and does.', key: { level: 1 }, group: 'torn' },
  { id: 'respect', featureId: 'web-respect', name: 'Respect calculator', path: '/respect', blurb: 'Respect for a hit or a whole chain: level, fair fight, war, retaliation, bonus hits.', key: { level: 1 }, group: 'torn' },
  { id: 'elo', featureId: 'web-elo', name: 'Elo calculator', path: '/elo', blurb: 'Your attack Elo: what a win gains and a loss costs against anyone, and the whole breakdown by your opponent’s Elo.', key: { level: 1 }, group: 'torn' },
  { id: 'spies', featureId: 'web-spies', name: 'Spies & estimates', path: '/spies', blurb: 'Omni-Torn’s estimate of any player or a whole faction, with its history; spies you and your faction recorded.', key: { level: 1 }, group: 'tools' },
  { id: 'loot', featureId: 'web-loot', name: 'NPC loot', path: '/loot', blurb: 'When each loot NPC reaches each loot level, with Loot Rangers’ attack time beside it.', key: { level: 1 }, group: 'tools' },
  // Website roadmap S03 (FF Scouter's target finder): Omni-Torn's player database, your own fair fight (a Limited key reads
  // your battle stats; only a rough band goes to the server), live checks with your key on your click.
  { id: 'targets', featureId: 'web-targets', name: 'Target finder', path: '/targets', blurb: 'Players to attack — levelling, respect for chains or your own filters — with fair fight, estimates and live status.', key: { level: 1 }, group: 'tools' },
  // Website roadmap S25 (FF Scouter's leaderboards): built once a day on the server (apps/api/src/leaderboards.ts) — Omni-Torn's
  // estimates (rounded), Torn's Hall of Fame, public-stat sightings from senders on terms 13, factions, activity; invite-only.
  { id: 'leaderboards', featureId: 'web-leaderboards', name: 'Leaderboards', path: '/leaderboards', blurb: 'Top players by Omni-Torn’s estimate and by public stats, top factions and the most active — all time, this quarter or during Torn’s events.', key: { level: 1 }, group: 'tools' },
  // Website roadmap S28 (TornStats' simulators, Torn Intel's upgrade simulator, weav3r's travel estimator): worked out in the
  // browser (packages/shared torn-sims, gym, combat, abroad, faction-upgrades); Torn asked only with your own key, on a click (or, for the public upgrade tree, when the Upgrades tab opens — once a day).
  { id: 'calc', featureId: 'web-calc', name: 'Calculators', path: '/calc', blurb: 'Merit planner, respect for a hit or a chain, gym gains, happy jumps, travel profit, fights (ProxiQuality-style) and faction upgrade costs — with your own numbers.', key: { level: 1 }, group: 'tools' },
  // Website roadmap S34 (Torn Intel Platinum's alerts, weav3r's favorites): your alert rules, kept with your lists (lists
  // sync — nothing new stored); the server checks them on what it already has and sends them where you chose.
  { id: 'alerts', featureId: 'web-alerts', name: 'Alerts', path: '/alerts', blurb: 'Alerts for stock prices and moves, your favorites’ target prices, stock abroad and “leave now”, your bank investment and landing — to the extension, Discord or this website.', key: { level: 1 }, group: 'site' },
  { id: 'account', featureId: 'web-account', name: 'Account & sync', path: '/account', blurb: 'Your key, level, what you share, the extension, your data.', key: { level: 1 }, always: true, group: 'site' },
  { id: 'owner', featureId: 'web-owner', name: 'Owner', path: '/owner', blurb: 'Owner tools.', key: { level: 1 }, access: 'owner', group: 'site' },
  // Owner only (owner's call 2026-11-02): the Discord bot's numbers — servers, commands seen, giveaways, look-ups, alerts.
  { id: 'bot-stats', featureId: 'web-bot-stats', name: 'Bot stats', path: '/bot-stats', blurb: 'The Discord bot’s numbers (owner only).', key: { level: 1 }, access: 'owner', group: 'site' },
];

export const SECTION_GROUPS: Record<WebSection['group'], string> = { you: 'You', faction: 'Faction', tools: 'Tools', torn: 'Torn', site: 'Omni-Torn' };

/** Overview cards: each locks on its own. */
export const OVERVIEW_CARDS = {
  status: { level: 1, user: ['profile'] },
  bars: { level: 2, user: ['bars'] },
  cooldowns: { level: 2, user: ['cooldowns'] },
  travel: { level: 2, user: ['travel'] },
  refills: { level: 2, user: ['refills'] },
  money: { level: 3, user: ['money'] },
  networth: { level: 3, user: ['networth'] },
} as const satisfies Record<string, KeyNeed>;

export function sectionByPath(path: string): WebSection | null {
  const p = path.replace(/\/+$/, '') || '/';
  return WEB_SECTIONS.find((s) => s.path === p || (s.path !== '/' && p.startsWith(`${s.path}/`))) ?? null;
}

export interface Viewer {
  key: KeyAccess | null;
  level: Level;
  factionId: number | null;
  access: AccessTable;
  /** This player's own overrides from the owner (Owner → Who gets what → This player). */
  mine?: MyAccess;
}

export type SectionState =
  | { open: true }
  | { open: false; why: 'key'; needs: KeyLevel; text: string }
  | { open: false; why: 'level'; text: string }
  | { open: false; why: 'faction'; text: string };

/** May this viewer open this section, and if not, why (for the lock note). */
export function sectionState(s: WebSection, v: Viewer): SectionState {
  if (!s.always && v.level !== 'owner') {
    // Closed until the owner opens it: without access, only Account (your key, tracking, your data).
    const mine = v.mine ?? NO_OVERRIDES;
    if (!canUseFeature(v.level, { id: s.featureId, ...(s.access ? { access: s.access } : {}) }, v.access, mine)) {
      if (!mayUseOmniTorn(v.level, v.access, mine) && !mine.deny.includes(s.featureId)) return { open: false, why: 'level', text: 'Not open yet — woyzer gives access to players one by one' };
      return { open: false, why: 'level', text: s.access === 'owner' ? 'Owner only' : 'Not part of your Omni-Torn level yet' };
    }
  }
  if (!keyMeets(v.key, s.key)) return { open: false, why: 'key', needs: s.key.level, text: keyNeedText(v.key, s.key) };
  if (s.faction && !v.factionId) return { open: false, why: 'faction', text: 'Join a faction to use this' };
  return { open: true };
}

/** Sections the access table hides completely (level), so a menu can leave them out instead of showing a lock. */
export function hiddenByLevel(s: WebSection, v: Viewer): boolean {
  const st = sectionState(s, v);
  return !st.open && st.why === 'level' && s.access === 'owner';
}

// ---------------------------------------------------------------- history snapshots

/**
 * One moment of a player's own numbers, kept on the server only for them (if they said yes),
 * at most one an hour: what the Personal stats and Battle stats charts are drawn from.
 */
export interface Snapshot {
  /** ms */
  at: number;
  level?: number;
  stats?: { strength: number; defense: number; speed: number; dexterity: number; total: number };
  networth?: number;
  /** Cash on hand, points, city bank, vault (whatever the key could read); bankUntil: when the bank investment ends (ms). */
  money?: { wallet?: number; points?: number; bank?: number; vault?: number; bankUntil?: number };
  /** Personal stats, flat (Torn's own names), numbers only. */
  ps?: Record<string, number>;
}

const MAX_PS = 400;
const PS_KEY = /^[a-z0-9_]{1,48}$/;
const num = (x: unknown, max = 1e16): number | null => {
  const n = typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x) ? Number(x) : x;
  return typeof n === 'number' && Number.isFinite(n) && Math.abs(n) <= max ? n : null;
};

/** Torn's personal stats, any shape (v1 flat; v2 nested by category) → flat `name: number`. */
export function flattenPersonalStats(x: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  const walk = (o: unknown, prefix: string, depth: number) => {
    const ob = obj(o);
    if (!ob || depth > 4) return;
    for (const [k, v] of Object.entries(ob)) {
      if (Object.keys(out).length >= MAX_PS) return;
      const name = (prefix ? `${prefix}_${k}` : k).toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 48);
      const n = num(v);
      if (n !== null) {
        if (PS_KEY.test(name)) out[name] = n;
      } else if (obj(v)) walk(v, prefix ? name : k === 'personalstats' ? '' : name, depth + 1);
    }
  };
  walk(x, '', 0);
  return out;
}

/** A snapshot from one v1 `/user/?selections=profile,personalstats,battlestats,networth,money` answer (any of them may be missing). */
export function buildSnapshot(body: unknown, at: number): Snapshot | null {
  const b = obj(body);
  if (!b || b.error) return null;
  const snap: Snapshot = { at };
  const level = num(b.level ?? obj(b.profile)?.level, 1000);
  if (level !== null) snap.level = level;
  const bs = obj(b.battlestats) ?? b;
  const stat = (k: string) => num(obj(bs[k])?.value ?? bs[k]);
  const [strength, defense, speed, dexterity] = [stat('strength'), stat('defense'), stat('speed'), stat('dexterity')];
  if (strength !== null && defense !== null && speed !== null && dexterity !== null) snap.stats = { strength, defense, speed, dexterity, total: strength + defense + speed + dexterity };
  const nw = obj(b.networth);
  const total = num(nw?.total);
  if (total !== null) snap.networth = total;
  const money: NonNullable<Snapshot['money']> = {};
  const wallet = num(b.money_onhand ?? obj(b.money)?.wallet);
  if (wallet !== null) money.wallet = wallet;
  const points = num(b.points ?? obj(b.money)?.points);
  if (points !== null) money.points = points;
  const cityBank = obj(b.city_bank) ?? obj(obj(b.money)?.city_bank);
  const bank = num(cityBank?.amount);
  if (bank !== null) money.bank = bank;
  // When the investment ends (v1 `time_left` in seconds; v2 `until` in unix seconds) — the Alerts page's bank alert (S34).
  const left = num(cityBank?.time_left);
  const until = num(cityBank?.until);
  if (bank && bank > 0 && left !== null && left > 0) money.bankUntil = at + Math.round(left) * 1000;
  else if (bank && bank > 0 && until !== null && until > 1e9) money.bankUntil = until < 1e12 ? until * 1000 : until;
  const vault = num(b.vault_amount ?? obj(b.money)?.vault);
  if (vault !== null) money.vault = vault;
  if (Object.keys(money).length) snap.money = money;
  const ps = flattenPersonalStats(b.personalstats);
  if (Object.keys(ps).length) snap.ps = ps;
  return Object.keys(snap).length > 1 ? snap : null;
}

/** A snapshot from outside (a request): only known fields with sane values. */
/**
 * `bankUntil`: keep when the bank investment ends (only for players on terms 16 — ALERTS_TERMS in alerts.ts; the server's
 * MeStore says so). Off by default.
 */
export function cleanSnapshot(x: unknown, at: number, opts: { bankUntil?: boolean } = {}): Snapshot | null {
  const o = obj(x);
  if (!o) return null;
  const snap: Snapshot = { at };
  const level = num(o.level, 1000);
  if (level !== null && level >= 1) snap.level = Math.round(level);
  const st = obj(o.stats);
  if (st) {
    const v = ['strength', 'defense', 'speed', 'dexterity'].map((k) => num(st[k]));
    if (v.every((n) => n !== null && n >= 0)) {
      const [strength, defense, speed, dexterity] = v as number[];
      snap.stats = { strength: strength!, defense: defense!, speed: speed!, dexterity: dexterity!, total: strength! + defense! + speed! + dexterity! };
    }
  }
  const nw = num(o.networth);
  if (nw !== null) snap.networth = nw;
  const m = obj(o.money);
  if (m) {
    const money: NonNullable<Snapshot['money']> = {};
    for (const k of ['wallet', 'points', 'bank', 'vault'] as const) {
      const n = num(m[k]);
      if (n !== null && n >= 0) money[k] = n;
    }
    {
      const u = num(m.bankUntil);
      if (opts.bankUntil && u !== null && u > at - 86_400_000 && u < at + 400 * 86_400_000) money.bankUntil = Math.round(u);
    }
    if (Object.keys(money).length) snap.money = money;
  }
  const ps = obj(o.ps);
  if (ps) {
    const kept: Record<string, number> = {};
    for (const [k, v] of Object.entries(ps).slice(0, MAX_PS)) {
      const n = num(v);
      if (PS_KEY.test(k) && n !== null) kept[k] = n;
    }
    if (Object.keys(kept).length) snap.ps = kept;
  }
  return Object.keys(snap).length > 1 ? snap : null;
}

/** The change of one number from the newest snapshot at or before `since` to `now` (null: no data that old). */
export function changeSince(history: readonly Snapshot[], pick: (s: Snapshot) => number | undefined, since: number, now: number | undefined): number | null {
  if (now === undefined) return null;
  let base: number | undefined;
  for (const s of history) {
    if (s.at > since) break;
    const v = pick(s);
    if (v !== undefined) base = v;
  }
  if (base === undefined) {
    // Nothing that old: the oldest we have, if it's within a day of `since` (a fresh account).
    const first = history.find((s) => pick(s) !== undefined);
    if (!first || first.at - since > 86_400_000) return null;
    base = pick(first);
  }
  return base === undefined ? null : now - base;
}

// ---------------------------------------------------------------- preferences

/** The member-share fields a player can pick (the extension's core/member-share.ts SHARE_FIELDS keys). */
export const SHARE_KEYS = ['bars', 'cooldowns', 'refills', 'travel', 'status', 'stats', 'weapons'] as const;
export type ShareKey = (typeof SHARE_KEYS)[number];
/** On by default when sharing is switched on (as the extension's defaults). */
export const SHARE_DEFAULTS: readonly ShareKey[] = ['bars', 'cooldowns', 'refills', 'travel', 'status'];
/** What "Your faction" shares from the server when the player never chose (owner's call 2026-10-19: everything the extension shares, with their battle stats total and loadout). */
export const FACTION_SHARE_DEFAULTS: readonly ShareKey[] = [...SHARE_DEFAULTS, 'stats', 'weapons'];
export const SHARE_LABELS: Record<ShareKey, string> = {
  bars: 'Energy, nerve, happy and life',
  cooldowns: 'Drug, booster and medical cooldowns',
  refills: 'Whether your refills are used today',
  travel: 'Exact flight times',
  status: 'Status (okay, hospital, jail — until when)',
  stats: 'Battle stats total',
  weapons: 'Your loadout: weapons and armor (from your Items page, or Torn’s API with “Your faction”)',
};

/**
 * A player's website preferences, kept on the server and followed by the extension too
 * (one set: change it on either and the other follows). `updatedAt` decides which side wins.
 */
export interface WebPrefs {
  /** Keep a history of my stats (null: never answered — counts as yes; false: no). */
  history: boolean | null;
  /**
   * Track my account in the background: the server keeps the key I signed in with (encrypted)
   * and keeps my data up to date even when neither the website nor the extension is open.
   * On unless switched off; off removes the key from the server.
   */
  track: boolean;
  /** Share with my faction: on, and which fields. */
  share: { on: boolean; fields: ShareKey[] };
  /**
   * When the player last chose their sharing themselves (website, extension, "Delete my data"); 0 or missing: never —
   * the defaults apply (report 023500-0ad6: `updatedAt` also moves when only tracking changes, e.g. a key given in
   * Discord, which froze the old "off" default and stopped a bot key's faction share).
   */
  shareAt?: number;
  /** The terms version (key-terms TERMS_VERSION) they agreed to; 0/missing: version 1 (before 2026-10-21). */
  terms?: number;
  /** "Share my spies with every Omni-Torn user" (spies.ts SPY_SHARE_TERMS, `spiesForEveryone`): off unless true. */
  shareSpies?: boolean;
  updatedAt: number;
}

/**
 * Sharing as it really applies: agreed to the terms that make faction sharing part of Omni-Torn (version 2, owner's call
 * 2026-10-21) → everything is shared, no switch; before that, what they chose under version 1.
 */
export function effectivePrefs(p: WebPrefs): WebPrefs {
  return (p.terms ?? 0) >= FACTION_SHARE_TERMS ? { ...p, share: { on: true, fields: [...SHARE_KEYS] } } : p;
}

/** The terms version that made faction sharing part of Omni-Torn (2) and the one that shares loadouts seen in fights with everyone (3). */
export const FACTION_SHARE_TERMS = 2;
export const PUBLIC_LOADOUT_TERMS = 3;

/** Agreed to the current terms? */
export const termsAgreed = (p: Pick<WebPrefs, 'terms'>): boolean => (p.terms ?? 0) >= TERMS_VERSION;
/** The loadouts they see in fights go to every Omni-Torn user (terms version 3)? (Their own stays with their faction.) */
export const loadoutsForEveryone = (p: Pick<WebPrefs, 'terms'>): boolean => (p.terms ?? 0) >= PUBLIC_LOADOUT_TERMS;

/** Has the player chosen their faction sharing themselves (else the defaults apply)? */
export const shareChosen = (p: Pick<WebPrefs, 'shareAt'>): boolean => (p.shareAt ?? 0) > 0;

// Sharing with your faction is on until you switch it off (owner's call 2026-10-20: website sign-ins share too).
export const DEFAULT_PREFS: WebPrefs = { history: null, track: true, share: { on: true, fields: [...SHARE_DEFAULTS] }, shareAt: 0, updatedAt: 0 };

/** History is kept unless the player said no. */
export const historyOn = (p: Pick<WebPrefs, 'history'>): boolean => p.history !== false;

export function cleanPrefs(x: unknown, now: number): WebPrefs {
  const o = obj(x);
  const share = obj(o?.share);
  const fields = Array.isArray(share?.fields) ? [...new Set(share!.fields.filter((f): f is ShareKey => (SHARE_KEYS as readonly unknown[]).includes(f)))] : [...SHARE_DEFAULTS];
  const updatedAt = num(o?.updatedAt);
  const shareAt = num(o?.shareAt);
  const terms = num(o?.terms);
  return {
    history: o?.history === true ? true : o?.history === false ? false : null,
    track: o?.track !== false,
    share: { on: share?.on === true, fields },
    shareAt: shareAt !== null && shareAt > 0 && shareAt <= now + 60_000 ? shareAt : 0,
    terms: terms !== null && terms >= 0 && terms <= TERMS_VERSION ? Math.floor(terms) : 0,
    ...(o?.shareSpies === true ? { shareSpies: true } : {}),
    updatedAt: updatedAt !== null && updatedAt > 0 && updatedAt <= now + 60_000 ? updatedAt : now,
  };
}

// ---------------------------------------------------------------- the faction share

/** What each share field needs from the key (weapons: the extension only, from the Items page). */
export const SHARE_NEEDS: Record<ShareKey, KeyNeed | null> = {
  bars: { level: 2, user: ['bars'] },
  cooldowns: { level: 2, user: ['cooldowns'] },
  refills: { level: 2, user: ['refills'] },
  travel: { level: 2, user: ['travel'] },
  status: { level: 1, user: ['profile'] },
  stats: { level: 3, user: ['battlestats'] },
  weapons: null,
};

/**
 * Why a "Your faction" key shares less than a mate would expect (bug 174817-927f: a bot-key member showed no bars or
 * flight on their profile), in plain words — or null when nothing's missing. `shares`: given since that use shares
 * (keys from before aren't shared until given again — the text they agreed to didn't say so).
 */
export function factionShareGap(k: { accessLevel: number; shares: boolean }): string | null {
  if (!k.shares) return 'Not shared with your faction mates yet: this key was given before “Your faction” shared bars and flights — give it again (with “Your faction” ticked) to share them.';
  if (k.accessLevel < 2) return 'A Public key: only your status is shared — bars, cooldowns and flight times need a Minimal key or better.';
  return null;
}

/** The v1 /user selections a share of these fields needs, as far as the key can read them. */
export function shareSelections(fields: readonly ShareKey[], key: KeyAccess | null): string[] {
  const out = new Set<string>();
  for (const f of fields) {
    const need = SHARE_NEEDS[f];
    if (need && keyMeets(key, need)) need.user!.forEach((x) => out.add(x));
  }
  return [...out];
}

/** A share (what /api/coord/share takes) from one v1 /user answer with those selections. */
export function shareFromUser(body: unknown, selections: readonly string[], now: number): Record<string, unknown> {
  const b = obj(body) ?? {};
  const has = (x: string) => selections.includes(x);
  const secs = (x: unknown) => {
    const v = num(x);
    return v && v > 0 ? now + v * 1000 : null;
  };
  const ms = (x: unknown) => {
    const v = num(x);
    return v && v > 0 ? (v < 1e12 ? v * 1000 : v) : null;
  };
  const data: Record<string, unknown> = { at: now };
  if (has('bars')) {
    const bars: Record<string, unknown> = {};
    for (const k of ['energy', 'nerve', 'happy', 'life']) {
      const o = obj(b[k]);
      const current = num(o?.current);
      const maximum = num(o?.maximum);
      if (current !== null && maximum !== null) bars[k] = { current, maximum, fullAt: secs(o?.fulltime) };
    }
    data.bars = bars;
  }
  if (has('cooldowns')) {
    const c = obj(b.cooldowns);
    if (c) data.cooldowns = { drug: secs(c.drug), booster: secs(c.booster), medical: secs(c.medical) };
  }
  if (has('refills')) {
    const r = obj(b.refills);
    if (r) data.refills = { energy: r.energy_refill_used === false, nerve: r.nerve_refill_used === false };
  }
  if (has('travel')) {
    const t = obj(b.travel);
    const left = num(t?.time_left) ?? 0;
    const destination = typeof t?.destination === 'string' ? t.destination : '';
    data.travel = t && destination && (left > 0 || destination !== 'Torn') ? { destination, departedAt: ms(t.departed), arrivalAt: left > 0 ? ms(t.timestamp) : null } : null;
  }
  if (has('profile')) {
    const st = obj(b.status);
    if (st && typeof st.state === 'string') data.status = { state: st.state, description: typeof st.description === 'string' ? st.description : '', until: ms(st.until) };
  }
  if (has('battlestats')) {
    const v = ['strength', 'defense', 'speed', 'dexterity'].map((k) => num(obj(b[k])?.value ?? b[k]));
    data.stats = v.every((x) => x !== null) ? (v as number[]).reduce((a, x) => a + x, 0) : null;
  }
  return data;
}

// ---------------------------------------------------------------- Torn's API terms

/** Torn's API terms ask for this table wherever a key is entered: the same one as the extension and the Discord bot (key-terms.ts). */
export const WEB_DISCLOSURE = TORN_TERMS_PAIRS;
